import { useCallback, useEffect, useLayoutEffect, useMemo } from "react";
import { View } from "react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { useLocale } from "@src/packages/i18n";
import type { ChatStackParamList } from "../../types";
import { getFactSheetRouteBlock } from "../../components/factSheetRouteIds";
import {
  MedicationDetailContent,
  type MedicationUserContext,
} from "../../components/MedicationFactsheetSheet";
import { useSmartBack } from "../../hooks/useSmartBack";

type Props = NativeStackScreenProps<ChatStackParamList, "MedicationDetail">;

const DEV_PATIENT_MEDICATION_ID = "15640000-0000-4000-8000-000000000001";

function getDevPatientMedicationContext(): MedicationUserContext | null {
  if (!__DEV__ || typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("dev_patient_medication_actions") !== "true") return null;
    return {
      originalName: params.get("dev_medication_name") ?? "Gonal-F",
      dosage: params.get("dev_medication_dose") ?? "150 IU",
      route: params.get("dev_medication_route") ?? "subcutaneous",
      selectedForm: "pre_filled_pen",
      timeTaken: [params.get("dev_medication_time") ?? "20:00"],
      startDate: params.get("dev_medication_start") ?? "2026-07-10",
      endDate: null,
      notes: params.get("dev_medication_notes") ?? "Evening stimulation dose",
    };
  } catch {
    return null;
  }
}

/**
 * Deep-linkable medication factsheet at /learn/medication/:medicationId.
 * Looks the content block up by id and renders the same detail view used by
 * the list's modal sheet. medicationId is the content-block id, e.g.
 * "med-menotropins" (Menopur) or "med-follitropin-alfa" (Gonal-F).
 */
export default function MedicationDetailScreen({ route, navigation }: Props) {
  const { medicationId, returnTab } = route.params;
  const { locale } = useLocale();
  const handleBack = useSmartBack({
    returnTab,
    fallbackTab: "Learn",
    fallbackScreen: "Medications",
  });

  // locale dep re-derives when the active content locale flips, picking up
  // translated bodies from registerLocaleVariant().
  const medication = useMemo(() => {
    void locale;
    return getFactSheetRouteBlock("medication", medicationId);
  }, [medicationId, locale]);
  const devPatientMedicationContext = useMemo(
    () => getDevPatientMedicationContext(),
    [],
  );
  const handleDevEditPreview = useCallback(() => {
    // Visual review only: real edit is exercised from saved medication rows.
  }, []);

  useEffect(() => {
    if (!medication) {
      navigation.goBack();
    }
  }, [medication, navigation]);

  useLayoutEffect(() => {
    if (medication) {
      navigation.setOptions({ title: medication.title });
    }
  }, [medication, navigation]);

  if (!medication) return null;

  return (
    <View style={{ flex: 1 }}>
      <MedicationDetailContent
        item={medication}
        onClose={handleBack}
        skipExitAnimation
        userContext={devPatientMedicationContext ?? undefined}
        medicationId={
          devPatientMedicationContext ? DEV_PATIENT_MEDICATION_ID : undefined
        }
        onEditPress={
          devPatientMedicationContext ? handleDevEditPreview : undefined
        }
      />
    </View>
  );
}
